/**
 * useCompletionChoreography — the completion morph CLOCK-OWNER (AV.W19 re-founding).
 *
 * ── DDR-AV-W19-MORPH-FIDELITY (ElementMorph kept as sole morph owner) ──
 * WHAT: ONE clock — the `ElementMorph` whose `play()` promise IS the
 *       single completion handle — fanning to the morph SPINE, the
 *       layout EVICTION, the celebration ENTER, and the reveal CASCADE.
 *       The composable owns the morph window (delegating the geometric
 *       play to keyframes.js's `ElementMorph`) and publishes an IMMEDIATE
 *       state-flip handle (`dataComplete` — `data-engine-state` + the
 *       morph TARGET seating) AND a single DEFERRED reveal-clock handle
 *       (`dataMorphResolved` + the readonly `revealActive` mirror) that
 *       opens the eviction, the celebration enter, AND the reveal cascade
 *       TOGETHER on the morph's own resolution.
 *
 * WHY (AV.W19, the I-1 three-clock collapse + the hidden-celebration fix):
 *   The completion ran THREE unreconciled clocks chained by side-effects:
 *   the canvas whirl signal-ref (`choreographyComplete`) → the 450ms
 *   `ElementMorph` Promise → the CSS attribute-flip reveal. WORSE, the
 *   most expressive beat played INVISIBLE: `CompleteBadge`/`CompleteHeadline`
 *   keyed their ~840ms staged enter on `isComplete` while their container
 *   sat `opacity:0` until the morph resolved — the disc scale-in / ring
 *   sweep / check draw all played UNDER a hidden container; only the
 *   static end-state was ever seen. The re-founding makes the morph the
 *   single post-whirl clock and RE-KEYS the celebration enter to the
 *   reveal clock (`revealActive`) so the seal draw is the WITNESSED
 *   climax — the badge choreography now plays DURING the reveal's
 *   opacity fade-in, in full view.
 *
 *   The route-VT axis (running↔result URL) is owned SEPARATELY by
 *   `useRouteTransition().navigate` (`types:["complete"]`/`["retake"]`)
 *   on the navigation callbacks — NOT here. ONE motion owner per axis:
 *   native VT owns the route URL transition; `ElementMorph` owns the
 *   meter→number geometric morph. The W2 shared `transitionKey` makes the
 *   route-level `<Transition>` inert across the running↔result flip, so
 *   there is no double-driving (DDR-AV-W19-MORPH-FIDELITY in PROGRESS.md).
 *
 * ── THE TARGET-RECT RACE — CLOSED (gate-7 ElementMorph branch) ─────────
 *   The TO-rect was sampled on a SINGLE deferred rAF (the ~40ms window
 *   the silent-cut bug lived in: the target `.complete-wrap` flips
 *   `absolute → static` on the same tick, so a one-frame read could
 *   sample the pre-settle geometry). The re-founding double-rAFs the
 *   sample AND re-measures via `ElementMorph.measure(from, to)` after a
 *   `ResizeObserver` settle, so the morph lands on the FINAL geometry
 *   regardless of when the layout flip commits.
 *
 * ── Vue surface ──────────────────────────────────────────────────────
 *   - `isComplete`        — `Readonly<Ref<boolean>>` mirroring
 *                           `useMetricStateMachine.allComplete` && the
 *                           meter whirl signal. The rising-edge source
 *                           for `data-engine-state="complete"` + the
 *                           morph start.
 *   - `dataComplete`      — `ComputedRef<"true"|"false">` for the
 *                           `:data-complete` attribute (attribute-friendly
 *                           string form, never the boolean that serialises
 *                           to "").
 *   - `dataMorphResolved` — `ComputedRef<"true"|"false">` for the
 *                           `:data-morph-resolved` attribute. Starts
 *                           "false"; flips "true" when the morph's
 *                           `play()` settles (or immediately under PRM /
 *                           missing source). The SINGLE reveal clock the
 *                           CSS eviction + reveal cascade key on.
 *   - `revealActive`      — `Readonly<Ref<boolean>>` — the SAME reveal
 *                           clock as a boolean, for the `:shown` prop on
 *                           `<ResultHeadlineCluster>`. THIS is the
 *                           hidden-celebration fix: the celebration enter
 *                           is keyed here, not on `isComplete`, so it
 *                           fires WHEN the reveal opens (visible), not
 *                           under the hidden container.
 *
 * ── Wiring (consumer) ────────────────────────────────────────────────
 * `SpeedtestResults.vue` calls `useCompletionChoreography({ source,
 * target })` with template refs for the morph SOURCE (`.meter-col`) and
 * TARGET (`.complete-wrap`); the composable installs the state-machine
 * read + the morph clock and returns the four handles. The dial-out CSS
 * slide layer (`[data-complete="true"] .meter-col { translateX(-24%);
 * opacity:0 }`) stays AS A FALLBACK only — the inline morph transform
 * supersedes it during the morph window; PRM / morph-failure paths still
 * get the CSS fallback (a morph throw can only ever degrade to "no
 * morph", never to "completion stranded" — the decoupling invariant).
 */

import {
    computed,
    onScopeDispose,
    readonly,
    ref,
    watch,
    type ComputedRef,
    type Ref,
} from "vue";
import { ElementMorph, springTimingFunction } from "@mkbabb/keyframes.js";

import { useChoreographyStore } from "@src/stores/useChoreographyStore.ts";
import { parseCssTimeMs } from "@design";
import { useMetricStateMachine } from "./useMetricStateMachine.ts";

/**
 * The canonical bouncy spring preset — matches glass-ui's `--spring-bouncy`
 * token (minted by `springLinearStops({ response: 0.5, dampingFraction:
 * 0.45 })`, peak ~+20.5% @14%). Sampled into the callable `TimingFunction`
 * `ElementMorph` consumes directly, so the completion morph rides the
 * same bouncy register as the system bouncy lanes.
 */
const BOUNCY_SPRING = springTimingFunction({
    response: 0.5,
    dampingFraction: 0.45,
});

/** Morph window fallback (matches `--duration-slow` = 450ms). */
const MORPH_DURATION_FALLBACK_MS = 450;

function readToken(name: string, fallback: string): string {
    if (typeof document === "undefined") return fallback;
    return (
        getComputedStyle(document.documentElement)
            .getPropertyValue(name)
            .trim() || fallback
    );
}

function prefersReducedMotion(): boolean {
    if (
        typeof window === "undefined" ||
        typeof window.matchMedia !== "function"
    ) {
        return false;
    }
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Is `node` a real DOM `Element` we can measure?
 *
 * W2-β-FIX guard. `<MeterColumn>` is a fragment component, so a mis-wired
 * SOURCE can resolve to its leading comment anchor (no
 * `getBoundingClientRect`). A morph attempt on it would throw inside the
 * completion reactive flush and strand the user on the running meter.
 * This predicate is the structural gate: only a node that is both an
 * `Element` and actually exposes `getBoundingClientRect` is morphable.
 */
function isMorphableElement(node: HTMLElement | null): node is HTMLElement {
    return (
        node instanceof Element &&
        typeof (node as Element).getBoundingClientRect === "function"
    );
}

/**
 * Swallow a decorative-morph failure. The morph is fire-and-forget polish;
 * a failure must never reach the user-visible completion flow, so it is
 * caught here and surfaced only in dev — never re-thrown. The
 * `dataMorphResolved` handle still flips to "true" on the catch path so
 * the CSS eviction + reveal still progress (the morph is decorative, the
 * completion is not — the AM W2-β decoupling invariant).
 */
function reportMorphFailure(err: unknown): void {
    if (import.meta.env.DEV) {
        console.error(
            "[useCompletionChoreography] morph skipped after failure:",
            err,
        );
    }
}

/** Drop the inline morph transform so the CSS end-state owns the box.
 *  Tolerant of a non-styled node (e.g. the `<MeterColumn>` fragment's
 *  leading comment anchor — no `.style`): there is no inline transform to
 *  clear on such a node, so it is a structural no-op. The `{ immediate:
 *  true }` watch (T1) runs this cleanup on the cold-mount falling-edge
 *  pass, where the wired source may legitimately be that anchor. */
function clearTransform(el: HTMLElement | null): void {
    if (!el || !el.style) return;
    el.style.transform = "";
    el.style.transformOrigin = "";
}

/**
 * AV.W19 — settle the morph TARGET's box before the morph reads its rect.
 *
 * The target (`.complete-wrap`) flips `position: absolute → static` on the
 * SAME reactive tick that starts the morph, so a single deferred `rAF` read
 * could sample the pre-settle geometry (the ~40ms silent-cut window). This
 * helper waits for the target's box to STOP changing, then calls back with
 * the live element so the morph re-measures against the final rect:
 *
 *   - a `ResizeObserver` resolves on the first post-flip resize callback
 *     (the layout has committed its new box) — the precise settle signal;
 *   - a double-`rAF` ceiling guarantees the callback fires even when the box
 *     never resizes (already static) or `ResizeObserver` is unavailable (the
 *     jsdom test env), so the morph path never stalls.
 *
 * Whichever fires FIRST wins (the observer disconnects on either path). The
 * `token` is the caller's play-generation guard: a settle that lands after a
 * later edge cancelled this play is dropped by the caller's `token` check.
 */
function settleTargetRect(
    target: HTMLElement,
    _token: number,
    onSettled: (live: HTMLElement) => void,
): void {
    let done = false;
    let observer: ResizeObserver | null = null;
    const finish = () => {
        if (done) return;
        done = true;
        observer?.disconnect();
        onSettled(target);
    };

    // ResizeObserver settle — fires once the post-flip box commits. Guarded
    // for the jsdom test env (no ResizeObserver) + a non-Element target.
    if (typeof ResizeObserver === "function" && target instanceof Element) {
        observer = new ResizeObserver(() => finish());
        observer.observe(target);
    }

    // Double-rAF ceiling — the box is already settled by the second frame in
    // the common case (the layout flip commits within one frame), and this
    // is the sole path when the box does not resize / ResizeObserver is
    // absent. `requestAnimationFrame` is stubbed synchronous in the unit
    // suite, so both frames resolve immediately there (one `morph` build).
    if (typeof requestAnimationFrame === "function") {
        requestAnimationFrame(() => requestAnimationFrame(() => finish()));
    } else {
        finish();
    }
}

export interface UseCompletionChoreographyOptions {
    /**
     * The morph SOURCE (`.meter-col`). When omitted the morph channel
     * is INERT — the composable still publishes `isComplete` +
     * `dataComplete` for the state read, and `dataMorphResolved` flips
     * immediately on the rising edge so the CSS reveal still cascades.
     * The inert path is what existing test mounts (which never wire a
     * morph) rely on for the contract probe.
     */
    source?: Ref<HTMLElement | null>;
    /** The morph TARGET (`.complete-wrap`). Same inert-when-omitted contract. */
    target?: Ref<HTMLElement | null>;
}

export interface UseCompletionChoreography {
    /** True iff every visible metric has finished AND the meter's farewell
     *  choreography has resolved. The single rising-edge source for both the
     *  `v-if="hero.isComplete.value"` headline mount AND the chassis
     *  `data-complete` attribute AND the morph clock start. */
    isComplete: Readonly<Ref<boolean>>;
    /** Attribute-friendly string mirror — bind to `:data-complete` on the card.
     *  Flips IMMEDIATELY on the rising edge so the `data-engine-state="complete"`
     *  layout flip seats the morph TARGET (`.complete-wrap` absolute→static)
     *  on the same tick the clock starts. */
    dataComplete: ComputedRef<"true" | "false">;
    /** Attribute-friendly string mirror — bind to `:data-morph-resolved` on
     *  the card. Starts "false"; flips to "true" when the morph `play()`
     *  settles (or immediately under PRM / on a missing source). CSS gates
     *  `.meter-col { display: none }` on this attribute — the SOURCE stays
     *  ALIVE through the morph window, then is evicted on the morph's own
     *  resolution. The reveal-cascade lanes also key off this flip so the
     *  arrival lands ON the delivered box. Falls back to "false" on the
     *  retake / falling edge so a second run starts from a clean morph. */
    dataMorphResolved: ComputedRef<"true" | "false">;
    /** The SAME reveal clock as a boolean — bind to `:shown` on
     *  `<ResultHeadlineCluster>`. AV.W19 hidden-celebration fix: the
     *  celebration staged enter (disc / ring / check, ~840ms) keys HERE,
     *  not on `isComplete`, so it fires WHEN the reveal opens (the seal
     *  draw plays in full view), never under the `opacity:0` container. */
    revealActive: Readonly<Ref<boolean>>;
}

export function useCompletionChoreography(
    options: UseCompletionChoreographyOptions = {},
): UseCompletionChoreography {
    const stateMachine = useMetricStateMachine();
    const choreography = useChoreographyStore();

    // AJ-W1-θ Path θ-A.1.a: `isComplete` gates on BOTH `allComplete` (the
    // engine-side condition) AND `choreographyComplete` (the meter-side
    // signal). The gate holds the running layout for the meter's farewell
    // whirl; the meter morphs out + the headline reveals once the
    // choreography signal resolves.
    const isComplete = computed(
        () =>
            stateMachine.allComplete.value && choreography.choreographyComplete,
    );

    const dataComplete = computed<"true" | "false">(() =>
        isComplete.value ? "true" : "false",
    );

    // ── The morph clock owner ────────────────────────────────────────
    // `morphResolved` is the DEFERRED handle — starts `false`, flips to
    // `true` when the morph promise settles. The CSS source-eviction +
    // reveal-cascade lanes key off this attribute (NOT off
    // `data-engine-state="complete"`). PRM + missing-source + morph-
    // failure paths all flip it `true` immediately so the CSS path
    // progresses honestly even when the morph itself is inert.
    const morphResolved = ref(false);
    const dataMorphResolved = computed<"true" | "false">(() =>
        morphResolved.value ? "true" : "false",
    );
    // The hidden-celebration fix (AV.W19): the celebration staged enter
    // keys on THIS — the SAME reveal clock the CSS cascade opens on — so
    // the seal draw plays WHEN the reveal opens (visible), not under the
    // `opacity:0` container the prior `isComplete`-keyed enter played under.
    const revealActive = readonly(morphResolved) as Readonly<Ref<boolean>>;

    // The morph instance the clock owns. Held so a re-test or scope
    // disposal can cancel a running play before constructing a new one.
    let morph: ElementMorph | null = null;

    // Track the latest morph play so a stale `.then()` resolution (from a
    // play that was cancelled by a later edge) can't flip the handle.
    let activePlayToken = 0;

    function startMorph(source: HTMLElement, target: HTMLElement): void {
        const token = ++activePlayToken;

        // Capture the source rect BEFORE the layout flip displaces it.
        const fromRect = source.getBoundingClientRect();

        // AV.W19 — CLOSE THE TARGET-RECT RACE (gate-7 ElementMorph branch).
        // The target (`.complete-wrap`) flips `absolute → static` on the
        // same `data-engine-state="complete"` tick that started this morph,
        // so a single deferred-rAF read could sample its PRE-settle box (the
        // ~40ms window the silent-cut bug lived in). Settle the TO-rect on a
        // double-rAF + a ResizeObserver-settle, THEN construct + play the
        // morph re-measured against the FINAL geometry. The source-eviction
        // still waits on `morphResolved` so the source stays painted across
        // the settle gap.
        settleTargetRect(target, token, (liveTo) => {
            try {
                if (token !== activePlayToken) return; // cancelled by a later edge

                if (!isMorphableElement(liveTo)) {
                    morphResolved.value = true;
                    return;
                }
                const toRect = liveTo.getBoundingClientRect();
                if (fromRect.width === 0 || toRect.width === 0) {
                    // Degenerate geometry — nothing to morph; flip the
                    // resolved handle so the eviction + reveal still fire.
                    morphResolved.value = true;
                    return;
                }

                const duration =
                    parseCssTimeMs(readToken("--duration-slow", "")) ??
                    MORPH_DURATION_FALLBACK_MS;

                morph?.stop();
                morph = new ElementMorph(fromRect, toRect, {
                    timingFunction: BOUNCY_SPRING,
                    duration,
                });
                void morph
                    .play(source)
                    .then(() => {
                        // Only the un-cancelled, natural completion flips
                        // the eviction handle — a stale cancel must leave
                        // the newer play's state in place. The inline
                        // transform clears regardless so the CSS end-state
                        // owns the box.
                        clearTransform(source);
                        if (token === activePlayToken) {
                            morphResolved.value = true;
                        }
                    })
                    .catch((err) => {
                        // Decorative-morph failure invariant: log + flip
                        // the resolved handle so the CSS eviction + reveal
                        // still progress; never re-throw. The completion
                        // is not decorative.
                        reportMorphFailure(err);
                        clearTransform(source);
                        if (token === activePlayToken) {
                            morphResolved.value = true;
                        }
                    });
            } catch (err) {
                reportMorphFailure(err);
                if (token === activePlayToken) {
                    morphResolved.value = true;
                }
            }
        });
    }

    // The ONE watcher — installs on `isComplete`, the single rising-edge
    // source. The rising edge starts the morph clock (if a source +
    // target are wired); the falling edge resets the resolved handle so
    // a re-test starts from a clean morph window.
    //
    // T1 (AV.W5, DEMONSTRATED gap): `{ immediate: true }` forces the
    // watcher's first read on registration, which is what makes a
    // SYNCHRONOUS terminal flip observable. The `?mock-results` dev path
    // jumps `isComplete` `false → true` in one tick (no per-phase walk);
    // a default (non-immediate) lazy `watch` over a `computed` does NOT
    // fire its callback for that jump when the computed reaches its
    // terminal value before the watcher's first dep-tracking read — the
    // rising-edge body never runs, `data-morph-resolved` stays "false",
    // and the `.complete-reveal` content renders at opacity 0 (measured
    // on the real edge: `?mock-results` → morphResolved "false"; the
    // `setPhase("complete")` dev-handle path, which mutates AFTER the
    // watcher is live, → "true"). `{ immediate: true }` closes the gap:
    // the first read forces dep-tracking AND, when the synchronous jump
    // has already landed, the immediate invocation (`prev === undefined`)
    // is admitted as the rising edge by the `if (prev) return` proxy
    // below. Cold mount (`isComplete === false`) takes the falling-edge
    // branch on that immediate run and returns early — no spurious morph.
    watch(
        isComplete,
        (complete, prev) => {
            if (!complete) {
                // Falling edge (re-test) OR the immediate cold-mount run
                // (`isComplete === false`): cancel any in-flight morph,
                // clear the inline transform, reset the handle. The
                // state-flip CSS lanes will themselves restore `.meter-col`
                // to its running posture on the next rising edge.
                activePlayToken += 1; // invalidate any in-flight .then()
                morph?.stop();
                morph = null;
                clearTransform(options.source?.value ?? null);
                morphResolved.value = false;
                return;
            }
            // `prev === undefined` is the immediate-run rising-edge proxy
            // (the synchronous jump already landed `true`); a truthy `prev`
            // is the steady-state retake guard. Both gate the morph start.
            if (prev) return; // not a rising edge — no-op

            // Rising edge. The morph wires + PRM gate decide whether the
            // CLOCK actually runs the morph spine. The resolved handle
            // either flips immediately (inert path — no morph) or waits on
            // the morph promise (active path).
            try {
                const from = options.source?.value ?? null;
                const to = options.target?.value ?? null;

                // Inert paths: PRM, no source, no target, non-Element. The
                // CSS reveal cascade still needs to land on the box, so flip
                // the resolved handle immediately. The CSS dial-out fallback
                // (the `[data-complete="true"] .meter-col` slide-out lane
                // in SpeedtestResults.vue) owns the source's farewell on
                // these paths.
                if (
                    prefersReducedMotion() ||
                    !isMorphableElement(from) ||
                    !isMorphableElement(to)
                ) {
                    morphResolved.value = true;
                    return;
                }

                // Active path — the morph clock runs the spine; the resolved
                // handle waits on `.then()`.
                startMorph(from, to);
            } catch (err) {
                // Any throw from the rising-edge body MUST be contained here
                // so completion is never stranded. Flip the resolved handle
                // so the CSS reveal still progresses (degrades to "no
                // morph", per the AM W2-β-FIX decoupling invariant).
                reportMorphFailure(err);
                morphResolved.value = true;
            }
        },
        { immediate: true },
    );

    // Teardown: an unmount mid-morph (re-test reset, route change)
    // cancels the running animation so no orphaned rAF loop keeps
    // mutating a detached element's transform.
    onScopeDispose(() => {
        activePlayToken += 1;
        morph?.stop();
        morph = null;
    });

    return {
        isComplete: readonly(isComplete) as Readonly<Ref<boolean>>,
        dataComplete,
        dataMorphResolved,
        revealActive,
    };
}
